import { getCampaign } from 'actions/campaigns'
import {
  getCampaignVariant,
  updateCampaignVariant,
} from 'actions/campaignVariants'
import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
// eslint-disable-next-line no-restricted-imports
import { DebouncedFunc } from 'lodash'
import debounce from 'lodash/debounce'
import { Fields as CampaignVariant } from 'models/CampaignVariant'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { replace } from 'react-router-redux'
import { selectCampaign } from 'selectors/campaign'
import { selectCampaignConfig } from 'selectors/campaignConfig'
import { selectCampaignVariant } from 'selectors/campaignVariant'
import { useAbility } from 'utilities/ability'

function useEditCampaign(campaignId: number, variantId: number) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const variant = useSelector(state => selectCampaignVariant(state, variantId))
  const campaign = useSelector(state => selectCampaign(state, campaignId))
  const campaignConfig = useSelector(state =>
    selectCampaignConfig(
      // @ts-ignore
      state,
      Number(campaignId),
      variant?.id
    )
  )
  const dispatch = useDispatch()
  const ability = useAbility()
  const currentMerchantUser = useCurrentMerchantUser()

  // Redirect if user lacks manage + ownership/collaboration rights
  useEffect(() => {
    if (!campaign) return
    const canManage = ability.can('manage', 'AllCampaigns')
    const isOwner =
      !!currentMerchantUser?.id &&
      campaign.creator?.id === currentMerchantUser.id
    const canEditOthers = ability.can('manage', 'OtherUsersCampaigns')
    if (!canManage || (!isOwner && !canEditOthers)) {
      dispatch(replace('/thanx_campaigns'))
    }
  }, [campaign, ability, currentMerchantUser, dispatch])

  useEffect(() => {
    async function fetch() {
      setIsLoading(true)
      await Promise.allSettled([
        dispatch(getCampaignVariant(campaignId, variantId)),
        dispatch(getCampaign(campaignId)),
      ])
      setIsLoading(false)
    }
    fetch()
  }, [campaignId, variantId, dispatch])

  async function triggerSave(model: Partial<CampaignVariant>) {
    if (!campaignId || !variantId || !variant) {
      return
    }

    const updates = {
      ...variant,
      ...model,
    }

    setIsSaving(true)
    await dispatch(updateCampaignVariant(campaignId, variantId, updates))
    setIsSaving(false)
  }

  const debouncedSave: DebouncedFunc<any> = useCallback(
    debounce(triggerSave, 750),
    []
  )

  // Cancel any pending debounced save when the editor unmounts, so a save
  // scheduled just before navigation doesn't fire after teardown (which also
  // leaked a stray PATCH across EditPush test renders).
  useEffect(() => () => debouncedSave.cancel(), [debouncedSave])

  return {
    campaign,
    variant,
    config: campaignConfig,
    isLoading,
    isSaving,
    triggerSave,
    debouncedSave,
  }
}

export { useEditCampaign }
